import React, { forwardRef, Suspense } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import Button from 'components/patterns/Button'
import strings from 'utils/strings'
import { isEmpty } from 'ramda'
import { classNames } from 'utils/utils'
import Alert from 'components/patterns/Alert'
import { Icon__Cancel as IconCancel } from 'components/patterns/Icon/components'

import {
  FooterStyling,
  StyledModal,
  ModalBounds,
  ModalBodyLayout,
  ModalHeaderLayout,
  ModalFooterLayout,
  ModalContentContainerLayout,
  ModalSideContainerLayout,
  ModalOverlay,
  ModalBannerLayout,
  ModalCloseButtonLayout,
} from './Modal.style'

const OldModal = ({
  closeButton,
  hideYesNoButtons,
  zIndex,
  invalid,
  children,
  noButtonText,
  customButtons,
  customFooter,
  maskClosable,
  yesButtonText,
  yesButtonType,
  yesButtonDisabled,
  yesButtonProps,
  noButtonProps,
  noButtonDisabled,
  header,
  isOpen,
  width,
  wrapClassName,
  noHandler,
  yesHandler,
  className,
  cancelBelow,
  size,
  cancelHandler,
  customFooterText,
}) => {
  let footer
  if (customFooter) {
    footer = customFooter
  } else if (hideYesNoButtons) {
    footer = null
  } else if (customButtons) {
    footer =
      cancelBelow && cancelHandler ? (
        <span className="flex space-between">
          <span className="link" onClick={cancelHandler}>
            {strings.cancel}
          </span>
          <span>
            {customButtons.map((button) => (
              <Button
                key={button.text + button.type}
                className={button.className}
                onClick={button.onClick}
                type={button.type}
                disabled={button.disabled}
                size={button.size || 'large'}
                loading={button.loading}
                iconProps={button.iconProps}
                doMargins={button.doMargins ?? false}
              >
                {button.text}
              </Button>
            ))}
          </span>
        </span>
      ) : (
        <>
          {!isEmpty(customFooterText) && customFooterText}
          {customButtons.map((button) => (
            <Button
              key={button.text + button.type}
              className={button.className}
              onClick={button.onClick}
              type={button.type}
              disabled={button.disabled}
              size={button.size || 'large'}
              loading={button.loading}
              iconProps={button.iconProps}
              doMargins={button.doMargins ?? false}
            >
              {button.text}
            </Button>
          ))}
        </>
      )
  } else if (cancelBelow) {
    footer = (
      <FooterStyling>
        <span
          className={'link'}
          onClick={() => (cancelHandler ? cancelHandler() : undefined)}
        >
          {strings.cancel}
        </span>
      </FooterStyling>
    )
  }

  const okButtonProps = {
    ...yesButtonProps,
    disabled: yesButtonDisabled || invalid,
  }
  const cancelButtonProps = { ...noButtonProps, disabled: noButtonDisabled }

  const widthBySize = {
    small: 480,
    default: 640,
    large: 964,
    extraLarge: 1200,
  }

  return (
    <StyledModal
      className={classNames('chec-modal', className)}
      destroyOnClose
      zIndex={zIndex}
      closable={closeButton}
      maskClosable={maskClosable}
      footer={footer}
      cancelText={noButtonText}
      okText={yesButtonText}
      okType={yesButtonType}
      okButtonProps={okButtonProps}
      cancelButtonProps={cancelButtonProps}
      title={header}
      visible={isOpen}
      width={width || widthBySize[size]}
      wrapClassName={wrapClassName}
      onCancel={noHandler}
      onOk={yesHandler}
    >
      {children}
    </StyledModal>
  )
}

OldModal.defaultProps = {
  className: '',
  customButtons: undefined,
  customFooter: undefined,
  closeButton: false,
  noButtonText: strings.no,
  yesButtonText: strings.yes,
  cancelBelow: false,
  maskClosable: false,
  size: 'default',
}

OldModal.propTypes = {
  closeButton: PropTypes.bool,
  hideYesNoButtons: PropTypes.bool,
  maskClosable: PropTypes.bool,
  isOpen: PropTypes.bool,
  cancelBelow: PropTypes.bool,
  yesButtonDisabled: PropTypes.bool,
  noButtonDisabled: PropTypes.bool,
  invalid: PropTypes.bool,

  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  customButtons: PropTypes.arrayOf(PropTypes.object),

  wrapClassName: PropTypes.string,
  className: PropTypes.string,
  yesButtonText: PropTypes.string,
  yesButtonType: PropTypes.string,
  // TODO: Define object shape
  yesButtonProps: PropTypes.object,
  noButtonProps: PropTypes.object,
  noButtonText: PropTypes.string,

  customFooter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
  customFooterText: PropTypes.node,

  header: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),

  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  size: PropTypes.oneOfType([
    PropTypes.oneOf(['small', 'default', 'large', 'extraLarge']),
    PropTypes.func,
  ]),

  noHandler: PropTypes.func,
  yesHandler: PropTypes.func,
  cancelHandler: PropTypes.func,

  zIndex: PropTypes.number,
}

export default OldModal

export function Modal({ size, children, Layout, 'data-testid': DataTestId }) {
  const CustomLayout = Layout || React.Fragment
  return createPortal(
    <CustomLayout>
      <ModalOverlay>
        <ModalBounds size={size} data-testid={DataTestId} data-modal-bounds>
          {children}
        </ModalBounds>
      </ModalOverlay>
    </CustomLayout>,
    document.body
  )
}

export const ModalLarge = (props) => <Modal {...props} size={ModalSizes.L} />
export const ModalMedium = (props) => <Modal {...props} size={ModalSizes.M} />
export const ModalSmall = (props) => <Modal {...props} size={ModalSizes.S} />
export const ModalCustom = (props) => <Modal {...props} size={ModalSizes.any} />

export function ModalHeader({ children, ...rest }) {
  return <ModalHeaderLayout {...rest}>{children}</ModalHeaderLayout>
}

ModalHeader.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
}

export function ModalCloseButton({ onClick, 'aria-label': ariaLabel }) {
  return (
    <Suspense fallback={null}>
      <ModalCloseButtonLayout
        onClick={onClick}
        aria-label={ariaLabel}
        style={{ marginLeft: 'auto' }}
      >
        <IconCancel />
      </ModalCloseButtonLayout>
    </Suspense>
  )
}

ModalCloseButton.propTypes = {
  onClick: PropTypes.func,
  'aria-label': PropTypes.string,
}

export const ModalBody = forwardRef(({ children, ...rest }, ref) => (
  <ModalBodyLayout ref={ref} {...rest}>
    {children}
  </ModalBodyLayout>
))

ModalBody.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
}

export function ModalFooter({ children, ...rest }) {
  return <ModalFooterLayout {...rest}>{children}</ModalFooterLayout>
}

ModalFooter.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
  className: PropTypes.string,
}

export function ModalContent(props) {
  const { style, className } = props
  return (
    <ModalContentContainerLayout style={style} className={className}>
      {props.children}
    </ModalContentContainerLayout>
  )
}

ModalContent.propTypes = {
  style: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
}

ModalContent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
}

export function ModalSideContainer(props) {
  return <ModalSideContainerLayout>{props.children}</ModalSideContainerLayout>
}

ModalSideContainer.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
}

export function ModalBanner(props) {
  return (
    <ModalBannerLayout>
      <Alert {...props} />
    </ModalBannerLayout>
  )
}

ModalBanner.defaultProps = {
  persistent: true,
}

ModalBanner.propTypes = {
  type: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  persistent: PropTypes.bool,
}

export function ModalButton(props) {
  return <Button size="large" doMargins={false} {...props} />
}

export const ModalSizes = {
  XL: 'XL',
  L: 'L',
  M: 'M',
  S: 'S',
  any: 'any',
}

Modal.propTypes = {
  size: PropTypes.oneOf(Object.values(ModalSizes)),
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  'data-testid': PropTypes.string,
}

Modal.defaultProps = {
  size: ModalSizes.XL,
}
